import { useEffect, useState, useMemo, useRef, memo } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { X, Navigation, AlertCircle } from 'lucide-react';

const RadarTiles = memo(function RadarTiles({
  originLat,
  originLng,
  targetLat,
  targetLng,
  targetIcon,
  isTargetOnScreen,
}: {
  originLat: number;
  originLng: number;
  targetLat: number;
  targetLng: number;
  targetIcon: L.DivIcon;
  isTargetOnScreen: boolean;
}) {
  return (
    <MapContainer
      center={[originLat, originLng]}
      zoom={18}
      zoomControl={false}
      attributionControl={false}
      className="w-full h-full"
      dragging={false}
      touchZoom={false}
      doubleClickZoom={false}
      scrollWheelZoom={false}
      boxZoom={false}
      keyboard={false}
      preferCanvas
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution=""
        updateWhenZooming={false}
        updateWhenIdle
        keepBuffer={2}
      />
      {isTargetOnScreen && (
        <Marker position={[targetLat, targetLng]} icon={targetIcon} />
      )}
    </MapContainer>
  );
});

interface GTARadarProps {
  userLat: number;
  userLng: number;
  userHeading: number;
  targetLat: number;
  targetLng: number;
  targetName: string;
  targetSeverity?: string;
  onClose?: () => void;
  className?: string;
  fullscreen?: boolean;
}


export default function GTARadar({
  userLat,
  userLng,
  userHeading,
  targetLat,
  targetLng,
  targetName,
  targetSeverity = 'medium',
  onClose,
  className = '',
  fullscreen = false
}: GTARadarProps) {
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [distance, setDistance] = useState<number>(0);
  const [targetBearing, setTargetBearing] = useState<number>(0);
  const [isTargetOnScreen, setIsTargetOnScreen] = useState<boolean>(false);

  // Rotation applied directly to DOM via rAF lerp — only while the heading is changing
  const mapRotateRef = useRef<HTMLDivElement>(null);
  const currentRotation = useRef<number>(0);
  const targetRotation = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const originRef = useRef({ lat: userLat, lng: userLng });

  const kickAnimate = () => {
    if (rafRef.current) return;
    const animate = () => {
      const el = mapRotateRef.current;
      const diff = targetRotation.current - currentRotation.current;
      if (el && Math.abs(diff) > 0.05) {
        currentRotation.current += diff * 0.12;
        el.style.transform = `rotate(${currentRotation.current}deg)`;
        rafRef.current = requestAnimationFrame(animate);
        return;
      }
      if (el) {
        currentRotation.current = targetRotation.current;
        el.style.transform = `rotate(${currentRotation.current}deg)`;
      }
      rafRef.current = 0;
    };
    rafRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    let diff = userHeading - (targetRotation.current % 360);
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    targetRotation.current = targetRotation.current + diff;
    kickAnimate();
  }, [userHeading]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // Calculate distance and bearing to target
  useEffect(() => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (targetLat - userLat) * Math.PI / 180;
    const dLon = (targetLng - userLng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(userLat * Math.PI / 180) * Math.cos(targetLat * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const dist = R * c;
    setDistance(dist);

    // Calculate bearing
    const y = Math.sin(dLon) * Math.cos(targetLat * Math.PI / 180);
    const x = Math.cos(userLat * Math.PI / 180) * Math.sin(targetLat * Math.PI / 180) -
      Math.sin(userLat * Math.PI / 180) * Math.cos(targetLat * Math.PI / 180) * Math.cos(dLon);
    let bearing = Math.atan2(y, x) * 180 / Math.PI;
    bearing = (bearing + 360) % 360;
    setTargetBearing(bearing);

    // Determine if target is on screen (within ~50m for close radar view)
    // Show edge marker when target is further than 50m
    setIsTargetOnScreen(dist < 50);
  }, [userLat, userLng, targetLat, targetLng]);

  // Target marker icon
  const targetIcon = useMemo(() => {
    const color = targetSeverity === 'critical' ? '#ef4444' : 
                  targetSeverity === 'high' ? '#f97316' : 
                  targetSeverity === 'medium' ? '#eab308' : '#3b82f6';
    return L.divIcon({
      className: 'custom-target-marker',
      html: `
        <div style="
          width: 20px;
          height: 20px;
          background: ${color};
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
            <path d="M12 2L2 22h20L12 2z"/>
          </svg>
        </div>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
  }, [targetSeverity]);

  // Calculate target position on radar edge when off-screen
  const edgeMarkerPosition = useMemo(() => {
    if (isTargetOnScreen) return null;
    
    // Calculate relative bearing (target direction relative to device heading)
    let relativeBearing = targetBearing - userHeading;
    relativeBearing = (relativeBearing + 360) % 360;
    
    // Convert to radians, adjust for CSS rotation (0 is up, clockwise)
    const angleRad = (relativeBearing - 90) * Math.PI / 180;
    
    // Position on edge of circle (radius ~40% from center)
    const radius = 40;
    const x = 50 + radius * Math.cos(angleRad);
    const y = 50 + radius * Math.sin(angleRad);
    
    return { x, y, rotation: relativeBearing };
  }, [targetBearing, userHeading, isTargetOnScreen]);

  if (!hasPermission) {
    return (
      <div className={`bg-card border border-border rounded-2xl p-6 text-center ${className}`}>
        <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-2">Location Permission Required</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Please enable location access to use the radar navigation.
        </p>
        <button
          onClick={() => setHasPermission(true)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${fullscreen ? 'fixed inset-0 z-[1000]' : ''} ${className}`}>
      {/* Radar Container */}
      <div 
        className={`
          relative overflow-hidden bg-black
          ${fullscreen ? 'w-full h-full aspect-square max-w-[min(100vw,100vh)] max-h-[min(100vw,100vh)] mx-auto rounded-full' : 'w-80 h-80 mx-auto rounded-full border-4 border-border'}
        `}
        style={{
          boxShadow: fullscreen ? 'none' : '0 8px 32px rgba(0,0,0,0.4)'
        }}
      >
        {/* Map - Rotates via direct DOM ref, no React re-renders */}
        <div 
          ref={mapRotateRef}
          className="absolute inset-0"
          style={{
            transformOrigin: 'center center'
          }}
        >
          <RadarTiles
            originLat={originRef.current.lat}
            originLng={originRef.current.lng}
            targetLat={targetLat}
            targetLng={targetLng}
            targetIcon={targetIcon}
            isTargetOnScreen={isTargetOnScreen}
          />
        </div>

        {/* Centered User Marker - Fixed at center with direction indicator */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="relative">
            {/* Blue dot - user position */}
            <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg" />
            {/* Direction triangle - always points up (forward direction) */}
            <div 
              className="absolute -top-2 left-1/2 -translate-x-1/2"
              style={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: '10px solid #3b82f6',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
              }}
            />
          </div>
        </div>

        {/* Radar Overlay Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Direction indicator at top (always points forward) */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2">
            <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-b-[12px] border-l-transparent border-r-transparent border-b-blue-500 drop-shadow-lg" />
          </div>

          {/* Range rings */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-1/2 h-1/2 rounded-full border border-white/20" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3/4 h-3/4 rounded-full border border-white/10" />
          </div>

          {/* Edge marker for off-screen target */}
          {!isTargetOnScreen && edgeMarkerPosition && (
            <div 
              className="absolute w-8 h-8 -ml-4 -mt-4 flex items-center justify-center"
              style={{
                left: `${edgeMarkerPosition.x}%`,
                top: `${edgeMarkerPosition.y}%`,
                transform: `rotate(${edgeMarkerPosition.rotation}deg)`,
              }}
            >
              <div className={`
                w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center
                ${targetSeverity === 'critical' ? 'bg-red-500' : 
                  targetSeverity === 'high' ? 'bg-orange-500' : 
                  targetSeverity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}
              `}>
                <Navigation className="w-3.5 h-3.5 text-white" style={{ transform: 'rotate(-45deg)' }} />
              </div>
            </div>
          )}

          {/* Distance indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-2 py-1 rounded-full font-medium">
            {distance > 1000 ? `${(distance / 1000).toFixed(1)} km` : `${Math.round(distance)} m`}
          </div>
        </div>

        {/* Target info bar */}
        <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full font-medium max-w-[80%] truncate">
          {targetName}
        </div>
      </div>

      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className={`
            absolute z-10 p-2 rounded-full bg-card border border-border shadow-lg
            ${fullscreen ? 'top-4 right-4' : '-top-2 -right-2'}
          `}
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Legend */}
      <div className={`flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground ${fullscreen ? 'absolute bottom-4 left-0 right-0' : ''}`}>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-blue-500" />
          <span>You</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`
            w-2 h-2 rounded-full
            ${targetSeverity === 'critical' ? 'bg-red-500' : 
              targetSeverity === 'high' ? 'bg-orange-500' : 
              targetSeverity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}
          `} />
          <span>Target</span>
        </div>
      </div>
    </div>
  );
}
